/*
  assets/pairs-well.js
  "Complete your routine" block inside the Added-to-Bag popup (#PopupAdded).

  Keeps the block full:
    - Merges candidates from 3 sources (in order, stops once there are enough):
        1. added product's metafield recommendations.frequently_bought_together
        2. Shopify complementary recommendations
        3. Shopify related recommendations
      Shows 3, keeps the rest as a backfill pool.
    - "Add to bag" adds in place: the popup is NOT rebuilt for the new item.
      The card shows "Added ✓", then is swapped for the next pool product.
      If the pool is empty the card stays as "In your bag ✓" (no gaps).
    - Popup subtotal / item count update live (needs the two data-attributes
      in theme-cart.liquid, see README notes).

  Theme hooks used (layout.theme.min.js, unchanged):
    cart:itemAdded  { item, cart }  -> theme renders popup, we fill the slot
    cart:updated    cart JSON       -> fired by window.AjaxCart.getCart()

  Load in layout/theme.liquid before </body>:
    <script src="{{ 'pairs-well.js' | asset_url }}" defer></script>
*/
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var POPUP = '#PopupAddedContainer';
  var SLOT = POPUP + ' .added-to-bag__pairs-well';
  var VISIBLE = 3;
  var TARGET_POOL = VISIBLE + 3;   // stop fetching sources once we have this many candidates
  var SWAP_DELAY = 1400;           // ms the "Added ✓" state stays before backfilling

  // Debug: add ?pwdebug to the URL once (remembered), or run localStorage.pwdebug = 1
  var DEBUG = false;
  try {
    if (/[?&]pwdebug/.test(location.search)) localStorage.setItem('pwdebug', '1');
    DEBUG = localStorage.getItem('pwdebug') === '1';
  } catch (e) {}
  function log() {
    if (!DEBUG) return;
    var args = ['%c[pairs-well]', 'color:#1f4d3a;font-weight:bold'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }
  log('script loaded');

  var requestId = 0;
  var pool = [];                   // hidden backfill cards
  var inCart = new Set();          // product ids currently in the bag

  /* ---------- fetching ---------- */

  function fetchBlock(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) log('HTTP', res.status, url, res.status === 404 ? '(section file missing in this theme, or product URL wrong)' : '');
        return res.ok ? res.text() : '';
      })
      .then(function (text) {
        if (!text) return null;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var dbg = doc.querySelector('[data-pw-debug]');
        log('source', url, '\n' + (dbg ? dbg.textContent.replace(/\\n/g, '\n') : '(no debug block: section did not render - check for a Liquid error)\n' + text.slice(0, 300)));
        var block = doc.querySelector('.pw');
        if (!block || !block.querySelector('.pw-card')) return null;
        return { block: block, style: doc.querySelector('style[data-pw-style]') };
      })
      .catch(function (err) { log('fetch failed', url, err); return null; });
  }

  function sourcesFor(item) {
    var rec = ROOT + 'recommendations/products?product_id=' + item.product_id +
      '&limit=10&section_id=pairs-well&intent=';
    return [
      ROOT + 'products/' + encodeURIComponent(item.handle) + '?section_id=pairs-well',
      rec + 'complementary',
      rec + 'related'
    ];
  }

  /* ---------- ranking: "nearest product that will convert" ----------
     score = relevance + review trust + price fit
       relevance   : your curated metafield > complementary > related,
                     + same scent / range words in the title,
                     - same product type as what was just added (routine = different step)
       review trust: Bayesian average, so 4.9 (8) does not beat 4.8 (203)
       price fit   : add-ons at or below the added item's price convert best
     Then the top 3 are picked with max 1 per product type (wash, lotion, conditioner...).
  */
  var W = {
    source: [3.0, 2.0, 1.0],   // metafield, complementary, related
    sharedWord: 1.2,           // per shared scent/range word, max 2 counted
    sameType: -2.5,            // same type as the added item (e.g. another body wash)
    hasQuote: 1.0,             // has a stored review quote: social proof converts better
    review: 2.5,               // multiplier on Bayesian rating above 4.0
    reviewVolume: 0.6,         // small bonus for lots of reviews (log scale)
    priceAtOrBelow: 1.0,
    priceUpTo1_5x: 0.3,
    priceAbove1_5x: -1.0
  };
  var PRIOR_RATING = 4.5;      // what an unknown product is assumed to be
  var PRIOR_WEIGHT = 20;       // how many reviews before a product's own score dominates

  var STOP_WORDS = new Set(('sukin botanical body wash hand lotion shampoo conditioner cream ' +
    'cleanser cleansing refill pump signature ml l 1l 500ml 500 250 125 1 2 and & the of with ' +
    'for natural hydrating moisturising moisturizing range pack set mini travel size x | -').split(' '));

  function words(text) {
    return (text || '').toLowerCase().replace(/[^a-z0-9& ]+/g, ' ').split(/\s+/)
      .filter(function (w) { return w.length > 2 && !STOP_WORDS.has(w); });
  }

  function scoreCard(card, item, sourceIndex) {
    var d = card.dataset;
    var rating = parseFloat(d.rating) || 0;
    var count = parseInt(d.count, 10) || 0;
    var price = parseInt(d.price, 10) || 0;
    var addedPrice = item.final_price || item.price || 0;
    var addedType = (item.product_type || '').trim().toLowerCase();
    var addedWords = new Set(words(item.product_title || item.title));

    var s = W.source[sourceIndex] || 0;
    if (card.querySelector('.pw-quote')) s += W.hasQuote;

    var shared = words(d.title).filter(function (w) { return addedWords.has(w); }).length;
    s += Math.min(shared, 2) * W.sharedWord;

    if (addedType && d.type && d.type === addedType) s += W.sameType;

    var bayes = (count * rating + PRIOR_WEIGHT * PRIOR_RATING) / (count + PRIOR_WEIGHT);
    s += (bayes - 4.0) * W.review;
    s += Math.log10(count + 1) * W.reviewVolume;

    if (addedPrice > 0 && price > 0) {
      var ratio = price / addedPrice;
      s += ratio <= 1 ? W.priceAtOrBelow : ratio <= 1.5 ? W.priceUpTo1_5x : W.priceAbove1_5x;
    }
    return { card: card, score: s, type: d.type || '', why: { src: sourceIndex, shared: shared, bayes: bayes.toFixed(2), price: price } };
  }

  async function collect(item) {
    var urls = sourcesFor(item);
    var results = await Promise.all(urls.map(fetchBlock)); // fetch all sources in parallel
    var shell = null;
    var seen = new Set();
    var scored = [];

    results.forEach(function (res, i) {
      if (!res) return;
      if (!shell) shell = res;
      res.block.querySelectorAll('.pw-card').forEach(function (card) {
        var id = card.dataset.productId;
        if (!id || seen.has(id) || inCart.has(id) || id === String(item.product_id)) return;
        seen.add(id);
        scored.push(scoreCard(card, item, i));
      });
    });

    scored.sort(function (a, b) { return b.score - a.score; });

    // Diversify: first pass max 1 per product type, then fill the rest in score order
    var picked = [];
    var usedTypes = new Set();
    scored.forEach(function (x) {
      if (picked.length < VISIBLE && (!x.type || !usedTypes.has(x.type))) {
        picked.push(x);
        if (x.type) usedTypes.add(x.type);
      }
    });
    scored.forEach(function (x) {
      if (picked.length < VISIBLE && picked.indexOf(x) === -1) picked.push(x);
    });
    var rest = scored.filter(function (x) { return picked.indexOf(x) === -1; });

    log('ranking', scored.map(function (x) {
      return { title: x.card.dataset.title, score: +x.score.toFixed(2), type: x.type, why: x.why };
    }));

    return { shell: shell, cards: picked.concat(rest).map(function (x) { return x.card; }) };
  }


  /* ---------- best review quote (Yotpo) ----------
     OFF by default: quotes come from the custom.review_highlight metafields.
     Only runs when window.PW_LIVE_REVIEWS = true. Then, for every card without a stored quote, loads that
     product's reviews from Yotpo's public widget API and picks the best one:
       - 5★ first (4★ only if no usable 5★), verified buyers preferred
       - short, complete sentence(s): 35-120 chars, never cut mid-sentence
       - talks about results / feel / scent (soft, hydrated, gentle, smells...)
       - no complaints or caveats ("but", "wish", "not", "rash", "leaked"...)
       - helpful votes and recent reviews win ties
     Same review is never used on two cards. Results cached per session.

     App key: auto-detected from the Yotpo script on the page, or set it in theme.liquid:
       <script>window.PW_YOTPO_APP_KEY = 'your-yotpo-app-key';</script>
  */
  var BENEFIT_WORDS = /\b(soft|softer|smooth|smoother|hydrat\w*|moistur\w*|nourish\w*|gentle|calm\w*|clean|fresh|glow\w*|silky|shiny|healthy|love|loved|amazing|beautiful|lovely|gorgeous|best|favourite|favorite|smell\w*|scent\w*|fragran\w*|absorb\w*|lightweight|non-?greasy|lather\w*|sensitive|dry skin|results?|difference|recommend\w*|repurchas\w*|holy grail)\b/gi;
  var NEGATIVE_WORDS = /\b(but|however|although|though|except|wish|unfortunately|didn'?t|doesn'?t|don'?t|wasn'?t|isn'?t|not|never|no longer|disappoint\w*|broke|broken|leak\w*|refund|return\w*|allerg\w*|rash\w*|burn\w*|sting\w*|itch\w*|irritat\w*|breakout|broke out|expensive|pricey|overpriced|watery|sticky|strong smell|too strong|meh|okay|ok)\b/i;
  var JUNK = /(https?:|www\.|@|\d{5,}|<|>|\*{2,}|!!!)/i;

  var usedReviewIds = new Set();
  var quoteCache = {};

  function yotpoKey() {
    if (window.PW_YOTPO_APP_KEY) return window.PW_YOTPO_APP_KEY;
    var scripts = document.querySelectorAll('script[src*="yotpo"]');
    for (var i = 0; i < scripts.length; i++) {
      var m = scripts[i].src.match(/yotpo\.com\/(?:v1\/loader\/|widget-loader\/)?([A-Za-z0-9]{20,})/);
      if (m) return m[1];
    }
    var el = document.querySelector('[data-appkey], [data-yotpo-app-key]');
    if (el) return el.getAttribute('data-appkey') || el.getAttribute('data-yotpo-app-key');
    return null;
  }

  function cleanText(html) {
    var t = document.createElement('textarea');
    t.innerHTML = String(html || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ');
    return t.value.replace(/\s+/g, ' ').trim();
  }

  // Best 1-2 consecutive complete sentences within length limits
  function bestSnippet(text) {
    var sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
    var best = null;
    for (var i = 0; i < sentences.length; i++) {
      for (var n = 1; n <= 2 && i + n <= sentences.length; n++) {
        var snip = sentences.slice(i, i + n).join(' ').replace(/\s+/g, ' ').trim();
        if (snip.length < 35 || snip.length > 120) continue;
        if (NEGATIVE_WORDS.test(snip) || JUNK.test(snip)) continue;
        if (snip === snip.toUpperCase()) continue;               // ALL CAPS
        var hits = (snip.match(BENEFIT_WORDS) || []).length;
        var s = Math.min(hits, 3) * 2 - (i * 0.3);               // earlier sentences read better
        if (snip.length >= 45 && snip.length <= 100) s += 1.5;   // sweet spot for the box
        if (!best || s > best.s) best = { text: snip, s: s, hits: hits };
      }
    }
    if (best && !/[.!?]$/.test(best.text)) best.text += '.';
    return best;
  }

  function formatName(name) {
    name = (name || '').trim();
    if (!name || /anonymous|customer|^a\.?$/i.test(name)) return '';
    var parts = name.split(/\s+/);
    var first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    var last = parts.length > 1 ? ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.' : '';
    return first + last;
  }

  function pickBest(reviews) {
    var now = Date.now();
    var ranked = [];
    reviews.forEach(function (rv) {
      if (!rv || rv.score < 4 || usedReviewIds.has(rv.id)) return;
      if (rv.deleted || rv.sentiment === 'negative') return;
      var snip = bestSnippet(cleanText(rv.content));
      if (!snip) return;
      var ageDays = rv.created_at ? (now - new Date(rv.created_at).getTime()) / 864e5 : 9999;
      var s = snip.s;
      s += rv.score === 5 ? 4 : 0;
      s += rv.verified_buyer ? 3 : 0;
      s += Math.min(rv.votes_up || 0, 6) * 0.5 - Math.min(rv.votes_down || 0, 6) * 0.7;
      s += ageDays < 365 ? 1 : ageDays < 730 ? 0.5 : 0;
      s += formatName(rv.user && rv.user.display_name) ? 0.5 : 0;
      ranked.push({ s: s, rv: rv, text: snip.text });
    });
    ranked.sort(function (a, b) { return b.s - a.s; });
    return ranked[0] || null;
  }

  function fetchReviews(productId) {
    if (quoteCache[productId]) return quoteCache[productId];
    var cacheKey = 'pw-yotpo-' + productId;
    try {
      var hit = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (hit) return (quoteCache[productId] = Promise.resolve(hit));
    } catch (e) {}

    var key = yotpoKey();
    if (!key) { log('Yotpo app key not found: set window.PW_YOTPO_APP_KEY'); return Promise.resolve([]); }

    var url = 'https://api-cdn.yotpo.com/v1/widget/' + encodeURIComponent(key) + '/products/' +
      encodeURIComponent(productId) + '/reviews.json?per_page=50&page=1&sort=votes_up&direction=desc';

    quoteCache[productId] = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var reviews = (json && json.response && json.response.reviews) || [];
        var slim = reviews.map(function (rv) {
          return { id: rv.id, score: rv.score, content: rv.content, created_at: rv.created_at,
            verified_buyer: rv.verified_buyer, votes_up: rv.votes_up, votes_down: rv.votes_down,
            sentiment: rv.sentiment, deleted: rv.deleted, user: { display_name: rv.user && rv.user.display_name } };
        });
        try { sessionStorage.setItem(cacheKey, JSON.stringify(slim)); } catch (e) {}
        log('yotpo reviews', productId, slim.length);
        return slim;
      })
      .catch(function (err) { log('yotpo fetch failed', productId, err); return []; });
    return quoteCache[productId];
  }

  function quoteHtml(text, name, verified) {
    var esc = function (s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; };
    var badge = verified
      ? '<span class="pw-verified"><svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="8"/>' +
        '<path d="M4.6 8.2l2.2 2.2 4.6-4.8"/></svg>Verified Buyer</span>'
      : '';
    var caption = (name || badge) ? '<figcaption>' + (name ? '<span>' + esc(name) + '</span>' : '') + badge + '</figcaption>' : '';
    return '<figure class="pw-quote pw-quote--auto"><blockquote>&ldquo;' + esc(text) + '&rdquo;</blockquote>' + caption + '</figure>';
  }

  function fillQuote(card) {
    // Stored quote (metafield) is already rendered by Liquid -> nothing to do.
    // Metafield mode (default): quotes come only from custom.review_highlight, rendered by Liquid.
    // Set window.PW_LIVE_REVIEWS = true in theme.liquid to also fetch live Yotpo quotes
    // for products that don't have a stored one yet.
    if (window.PW_LIVE_REVIEWS !== true) return;
    if (!card || card.querySelector('.pw-quote') || card.dataset.pwQuoteDone) return;
    card.dataset.pwQuoteDone = '1';
    fetchReviews(card.dataset.productId).then(function (reviews) {
      if (!card.isConnected || card.querySelector('.pw-quote')) return;
      var best = pickBest(reviews);
      if (!best) { log('no usable review for', card.dataset.title); return; }
      usedReviewIds.add(best.rv.id);
      var btn = card.querySelector('.pw-add');
      var name = formatName(best.rv.user && best.rv.user.display_name);
      btn.insertAdjacentHTML('beforebegin', quoteHtml(best.text, name, true));
      log('quote', card.dataset.title, '->', best.text, '(score ' + best.s.toFixed(1) + ')');
    });
  }

  function fillQuotes(root) {
    usedReviewIds = new Set();
    root.querySelectorAll('.pw-card').forEach(fillQuote);
  }


  /* ---------- button helpers (match theme add-to-cart markup) ---------- */
  function setLabel(btn, text) {
    var label = btn.querySelector('[data-add-to-cart-text]');
    if (label) label.textContent = text; else btn.textContent = text;
  }
  function setLoading(btn, on) {
    var spinner = btn.querySelector('.spinner');
    btn.disabled = on;
    btn.classList.toggle('adding-to-cart-button', on);
    if (spinner) spinner.hidden = !on;
    if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
  }


  /* ---------- loading skeleton (shown while products are fetched) ----------
     Mirrors the real layout in pairs-well.liquid: header + 3 cards
     (image 42% at 4:5, title, price, stars, quote box, 38px button).
     Mobile/tablet: same horizontal scroll row as the real cards. */
  var SKELETON_CARDS = 3;
  var LOADER_CSS = [
    '.pw-skel{background:#f3f2ed;padding:30px 32px 28px}',
    '.pw-skel *{box-sizing:border-box}',
    '.pw-skel__head{display:flex;justify-content:space-between;align-items:flex-end;gap:32px;margin-bottom:20px;padding:0 8px}',
    '.pw-skel__grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}',
    '.pw-skel__card{display:flex;gap:14px;padding:10px;background:#fbfaf7;box-shadow:0 1px 2px rgba(29,43,37,.05)}',
    '.pw-skel__img{flex:0 0 42%;align-self:flex-start;aspect-ratio:4/5}',
    '.pw-skel__body{flex:1;min-width:0;display:flex;flex-direction:column;gap:8px}',
    '.pw-skel__quote{display:flex;flex-direction:column;gap:6px;padding:8px;background:#f1efe8;margin-top:2px}',
    '.pw-skel__btn{height:38px;margin-top:auto}',
    '.pw-sk{display:block;background:#e7e4da;position:relative;overflow:hidden}',
    '.pw-skel__quote .pw-sk{background:#e2dfd4}',
    '.pw-sk::after{content:"";position:absolute;inset:0;transform:translateX(-100%);',
      'background:linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent);animation:pw-shimmer 1.3s ease-in-out infinite}',
    '@keyframes pw-shimmer{to{transform:translateX(100%)}}',
    '@media (prefers-reduced-motion:reduce){.pw-sk::after{animation:none}}',
    '@media (max-width:989px){',
      '.pw-skel{padding:24px 0 18px;overflow:hidden}',
      '.pw-skel__head{flex-direction:column;align-items:flex-start;gap:8px;padding:0 12px;margin-bottom:16px}',
      '.pw-skel__head .pw-sk--sub{display:none}',
      '.pw-skel__grid{display:flex;gap:12px;overflow:hidden;padding:0 12px 14px}',
      '.pw-skel__card{flex:0 0 auto;width:340px;max-width:calc(100vw - 72px)}',
    '}',
    '@media (max-width:749px){.pw-skel__card{width:300px}.pw-skel__img{flex-basis:38%}}'
  ].join('');

  function ensureLoaderCss() {
    if (document.getElementById('pw-loading-css')) return;
    var st = document.createElement('style');
    st.id = 'pw-loading-css';
    st.textContent = LOADER_CSS;
    document.head.appendChild(st);
  }

  function bar(w, h, extra) {
    return '<span class="pw-sk' + (extra ? ' ' + extra : '') + '" style="width:' + w + ';height:' + h + 'px"></span>';
  }

  function skeletonCard() {
    return '<div class="pw-skel__card">' +
      '<span class="pw-sk pw-skel__img"></span>' +
      '<div class="pw-skel__body">' +
        bar('92%', 12) + bar('70%', 12) +            // title (2 lines)
        bar('34%', 14) +                              // price
        bar('62%', 12) +                              // stars + count
        '<div class="pw-skel__quote">' + bar('100%', 10) + bar('88%', 10) + bar('60%', 10) + bar('45%', 9) + '</div>' +
        '<span class="pw-sk pw-skel__btn" style="width:100%"></span>' +
      '</div></div>';
  }

  function showLoader(slot) {
    if (!slot) return;
    ensureLoaderCss();
    var cards = '';
    for (var i = 0; i < SKELETON_CARDS; i++) cards += skeletonCard();
    slot.innerHTML =
      '<div class="pw-loading pw-skel" role="status" aria-live="polite" aria-label="Loading recommended products">' +
        '<div class="pw-skel__head">' +
          '<div style="display:flex;flex-direction:column;gap:10px;width:260px;max-width:70%">' + bar('55%', 10) + bar('100%', 22) + '</div>' +
          '<div style="display:flex;flex-direction:column;gap:6px;width:240px">' + bar('100%', 10, 'pw-sk--sub') + bar('75%', 10, 'pw-sk--sub') + '</div>' +
        '</div>' +
        '<div class="pw-skel__grid">' + cards + '</div>' +
      '</div>';
  }

  function hideLoader(slot) {
    var l = slot && slot.querySelector('.pw-loading');
    if (l) l.remove();
  }

  /* ---------- rendering ---------- */

  function render(item) {
    var myId = ++requestId;
    pool = [];
    if (!document.querySelector(SLOT)) {
      log('slot NOT found in popup:', SLOT,
        '\n-> is <div class="added-to-bag__pairs-well"></div> in PopupAddedTemplate?',
        '\n-> or the gift-tier branch is showing instead of related_content');
      return;
    }
    log('slot found, fetching for', item.handle, '(product_id ' + item.product_id + ')');
    showLoader(document.querySelector(SLOT));

    collect(item).then(function (result) {
      if (myId !== requestId) return;
      var slot = document.querySelector(SLOT);
      log('result: candidates=' + result.cards.length, 'slotStillThere=' + !!slot);
      if (!slot) return;
      if (!result.shell || !result.cards.length) { hideLoader(slot); return; }  // nothing to show: remove loader, keep popup short

      var block = result.shell.block;
      var grid = block.querySelector('.pw-grid');
      grid.innerHTML = '';
      result.cards.slice(0, VISIBLE).forEach(function (c) { grid.appendChild(c); });
      grid.dataset.count = Math.min(result.cards.length, VISIBLE);
      pool = result.cards.slice(VISIBLE);

      var type = (item.product_type || '').trim().toLowerCase();
      var typeEl = block.querySelector('[data-pw-type]');
      if (type && typeEl) typeEl.textContent = type;

      slot.innerHTML = '';
      slot.appendChild(block);
      if (result.shell.style) slot.appendChild(result.shell.style);
      fillQuotes(block);
      if (window.PW_LIVE_REVIEWS === true) {
        pool.forEach(function (c) { if (!c.querySelector('.pw-quote')) fetchReviews(c.dataset.productId); }); // warm cache for backfill
      }
      document.dispatchEvent(new Event('currency:convert'));
    });
  }

  function backfill(card) {
    var next = null;
    while (pool.length && !next) {
      var candidate = pool.shift();
      if (!inCart.has(candidate.dataset.productId)) next = candidate;
    }
    var btn = card.querySelector('.pw-add');

    if (!next) {                         // nothing left: keep the card, no gap
      if (btn) setLabel(btn, 'In your bag ✓');
      return;
    }
    card.classList.add('is-leaving');
    setTimeout(function () {
      if (!card.isConnected) return;
      next.classList.add('is-entering');
      card.replaceWith(next);
      fillQuote(next);
      document.dispatchEvent(new Event('currency:convert'));
    }, 200);
  }

  /* ---------- popup subtotal / count ---------- */

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var txt = (window.Currency && typeof window.Currency.formatMoney === 'function')
      ? window.Currency.formatMoney(cents, fmt)
      : '$' + (cents / 100).toFixed(2);
    return '<span class="money">' + txt + '</span>';
  }

  function updatePopupTotals(cart) {
    var popup = document.querySelector(POPUP);
    if (!popup || !cart) return;
    var sub = popup.querySelector('[data-pw-subtotal]');
    var count = popup.querySelector('[data-pw-count]');
    if (sub && typeof cart.total_price === 'number') sub.innerHTML = formatMoney(cart.total_price);
    if (count && typeof cart.item_count === 'number') {
      count.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
    }
    document.dispatchEvent(new Event('currency:convert'));
  }

  function syncInCart(cart) {
    if (!cart || !cart.items) return;
    inCart = new Set(cart.items.map(function (i) { return String(i.product_id); }));
  }

  /* ---------- events ---------- */

  // Theme added an item (normal add-to-cart) -> fill the block
  document.addEventListener('cart:itemAdded', function (e) {
    var res = e.detail && e.detail.response;
    var item = res && res.item;
    log('cart:itemAdded', item);
    if (!item || !item.product_id || !item.handle) return;
    syncInCart(res.cart);
    setTimeout(function () { render(item); }, 0);
  });

  // Theme refreshed the cart -> keep popup totals + in-cart set current
  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.response;
    syncInCart(cart);
    updatePopupTotals(cart);
  });

  // Add to bag from the block: add in place, don't rebuild the popup
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.pw-add');
    if (!btn) return;
    e.preventDefault();
    var card = btn.closest('.pw-card');
    var variantId = Number(card && card.dataset.variantId);
    if (!variantId || btn.disabled) return;

    // Loading state: add theme class + show the theme spinner next to the label
    setLoading(btn, true);

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Unavailable');
          return data;
        });
      })
      .then(function () {
        inCart.add(card.dataset.productId);
        setLoading(btn, false);
        btn.disabled = true;                 // stays disabled until the card is swapped
        btn.classList.add('is-added');
        setLabel(btn, 'Added ✓');

        // Refresh mini cart, header count, gift tiers + fires cart:updated (popup totals)
        if (window.AjaxCart && typeof window.AjaxCart.getCart === 'function') {
          window.AjaxCart.getCart();
        } else {
          fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(updatePopupTotals);
        }
        setTimeout(function () { backfill(card); }, SWAP_DELAY);
      })
      .catch(function (err) {
        setLoading(btn, false);
        btn.disabled = true;
        setLabel(btn, 'Unavailable');
        btn.title = err.message || '';
        setTimeout(function () {
          btn.disabled = false;
          setLabel(btn, 'Add to bag');
          btn.removeAttribute('title');
        }, 2200);
      });
  });
})();
